import { useCallback, useMemo, useState } from 'react'
import type { CustomerRouteMode } from '../routes'
import type { CustomerTwoFactorChallengePayload } from '../types'
import type { ShopMessageKey } from '../../i18n'
import {
  INITIAL_CUSTOMER_AUTH_VALUES,
  TOTAL_SIGNUP_STEPS,
} from './customer-auth-form/validation'
import { buildCustomerAuthSetters } from './customer-auth-form/setters'
import {
  submitCustomerAuthForm,
  type CustomerAuthSubmitResult,
  type LoginInput,
  type LoginResult,
  type SignupInput,
} from './customer-auth-form/submit'
import { clearFieldErrorsFromMap, markTouchedFields } from './customer-auth-form/errorState'
import { useCustomerAuthLiveValidation } from './customer-auth-form/useLiveValidation'
import { useSignupStepNavigation } from './customer-auth-form/useSignupStepNavigation'
import type {
  CustomerAuthField,
  CustomerAuthFieldErrorMap,
  CustomerAuthValues,
} from './customer-auth-form/validation'

type AuthMode = Extract<CustomerRouteMode, 'login' | 'signup'>

/**
 * Optional behaviors for field updates in auth form state.
 */
type UpdateFieldOptions = {
  touch?: CustomerAuthField[]
  clear?: CustomerAuthField[]
}

export type { CustomerAuthField, CustomerAuthFieldErrorMap }

/**
 * Manages login/signup form state, validation lifecycle and submit orchestration.
 *
 * @param params Auth mode, navigation context and async auth handlers.
 * @returns Form state, field setters, step navigation and submit callback.
 */
export const useCustomerAuthForm = ({
  mode,
  nextPath,
  onClearError,
  onNavigate,
  onLogin,
  onLoginTwoFactorChallenge,
  onSignup,
  botProtectionProviderId,
}: {
  mode: AuthMode
  nextPath: string
  onClearError: () => void
  onNavigate: (targetPath: string) => void
  onLogin: (input: LoginInput) => Promise<LoginResult>
  onLoginTwoFactorChallenge: (session: CustomerTwoFactorChallengePayload) => void
  onSignup: (input: SignupInput) => Promise<boolean>
  botProtectionProviderId?: string | null
}) => {
  const [values, setValues] = useState<CustomerAuthValues>(INITIAL_CUSTOMER_AUTH_VALUES)
  const [signupStep, setSignupStep] = useState(1)
  const [fieldErrorKeys, setFieldErrorKeys] = useState<CustomerAuthFieldErrorMap>({})
  const [touchedFields, setTouchedFields] = useState<Partial<Record<CustomerAuthField, boolean>>>({})
  const [dirtyFields, setDirtyFields] = useState<Partial<Record<CustomerAuthField, boolean>>>({})
  const [signupStepAttempted, setSignupStepAttempted] = useState<number | null>(null)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [botProtectionToken, setBotProtectionToken] = useState('')

  const isSignup = mode === 'signup'

  const clearFieldErrors = useCallback((fields?: CustomerAuthField[]) => {
    setFieldErrorKeys((current) => clearFieldErrorsFromMap(current, fields))
  }, [])

  const markFieldsTouched = useCallback((fields: CustomerAuthField[]) => {
    setTouchedFields((current) => markTouchedFields(current, fields))
  }, [])

  const touchField = useCallback((field: CustomerAuthField) => {
    markFieldsTouched([field])
  }, [markFieldsTouched])

  const updateField = useCallback(<K extends CustomerAuthField>(
    field: K,
    value: CustomerAuthValues[K],
    options: UpdateFieldOptions = {},
  ) => {
    const touchFields = options.touch ?? []
    const clearFields = options.clear ?? touchFields

    if (touchFields.length > 0) {
      markFieldsTouched(touchFields)
    }
    setDirtyFields((current) => ({ ...current, [field]: true }))
    clearFieldErrors(clearFields)
    setValues((current) => ({ ...current, [field]: value }))
  }, [clearFieldErrors, markFieldsTouched])

  useCustomerAuthLiveValidation({
    isSignup,
    signupStep,
    values,
    touchedFields,
    dirtyFields,
    stepAttempted: isSignup && signupStepAttempted === signupStep,
    submitted: formSubmitted,
    setFieldErrorKeys,
  })

  const {
    goToNextSignupStep,
    goToPreviousSignupStep,
    goToSignupStep,
  } = useSignupStepNavigation({
    isSignup,
    signupStep,
    values,
    clearFieldErrors,
    markFieldsTouched,
    onClearError,
    setFieldErrorKeys,
    setFormSubmitted,
    setSignupStep,
    setSignupStepAttempted,
  })

  const submit = useCallback(async (): Promise<CustomerAuthSubmitResult> => {
    setFormSubmitted(true)
    const activeProviderId = botProtectionProviderId?.trim() ?? ''
    const token = botProtectionToken.trim()

    return submitCustomerAuthForm({
      isSignup,
      values,
      nextPath,
      onClearError,
      clearFieldErrors: () => clearFieldErrors(),
      setSignupStep,
      markFieldsTouched,
      setFieldErrorKeys,
      onLogin,
      onLoginTwoFactorChallenge,
      onSignup,
      onNavigate,
      botProtection:
        activeProviderId.length > 0
          ? {
              providerId: activeProviderId,
              token,
            }
          : undefined,
    })
  }, [
    botProtectionProviderId,
    botProtectionToken,
    clearFieldErrors,
    isSignup,
    markFieldsTouched,
    nextPath,
    onClearError,
    onLogin,
    onLoginTwoFactorChallenge,
    onNavigate,
    onSignup,
    values,
  ])

  const setters = buildCustomerAuthSetters(updateField)

  return {
    isSignup,
    ...values,
    signupStep,
    totalSignupSteps: TOTAL_SIGNUP_STEPS,
    fieldErrorKeys,
    ...setters,
    touchField,
    goToNextSignupStep,
    goToPreviousSignupStep,
    goToSignupStep,
    clearFieldErrors,
    botProtectionToken,
    setBotProtectionToken,
    submit,
    titleKey: useMemo<ShopMessageKey>(
      () => (isSignup ? 'customer.auth.signupTitle' : 'customer.auth.loginTitle'),
      [isSignup],
    ),
    subtitleKey: useMemo<ShopMessageKey>(
      () => (isSignup ? 'customer.auth.signupSubtitle' : 'customer.auth.loginSubtitle'),
      [isSignup],
    ),
  }
}
